import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import moment from 'moment-timezone';

import { ActionButton, Backdrop } from '../../components/Themed.js';
import { Dropdown } from 'mytime-common/custom-fields/common/dropdown/dropdown.jsx';
import { getVariations } from '../../data/service/ServiceActions.js';
import { clearClientEmployee, setClientEmployee } from '../../data/client/ClientActions';
import {
  createAppointment,
  getStaffEstimations,
  generateSequence,
} from '../../data/appointment/AppointmentActions.js';
import { getEmployeeName } from '../../data/employee/EmployeeReducer.js';
import { translateToMins } from '../../common/utils.js';
import './StaffSelection.css';
import { getBookableVariations } from '../../data/service/ServiceReducer.js';
import { t } from '../../locales';

class StaffSelection extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      initialized: false,
      submitting: false,
      useNextAvailableStaffFlow: false,
    };

    this.continueClick = this.continueClick.bind(this);
    this.getClientEstimations = this.getClientEstimations.bind(this);
    this.allClientsHaveEstimations = this.allClientsHaveEstimations.bind(this);
    this.refreshEstimations = this.refreshEstimations.bind(this);
    this.getUniqueEstimationsForChildren = this.getUniqueEstimationsForChildren.bind(this);
    this.setEstimationsForChildren = this.setEstimationsForChildren.bind(this);
  }

  async componentDidMount() {
    const {
      clearClientEmployee,
    } = this.props;

    // Initialize employee id with 0 whenever user comes to staff selection (Next available is displayed by default)
    clearClientEmployee()

    await this.updateStaffEstimations();
    this.setState({ initialized: true });
  }

  async updateStaffEstimations() {
    const {
      bookRealAppointments,
      setClientEmployee,
      hasServicesForClient,
      childrenSelectedForServices,
      clientInformation,
      clientEmployee,
    } = this.props;

    const clientId = clientInformation.id;
    let clientEstimation = {}

    if (bookRealAppointments) {
      await this.refreshEstimations(true);

      if (hasServicesForClient) {
        const estimations = this.getClientEstimations(this.props.staffEstimations, clientId, null);

        if (estimations.length > 0 && !estimations[0].isUnavailable) {
          if (!this.state.useNextAvailableStaffFlow) {
            // By default, "Next available" option is chosen => employee id must be 0
            setClientEmployee(clientId, null, 0, estimations[0]);
            this.setState({ useNextAvailableStaffFlow: true })
          }
          else {
            // Find previous employee (because it's the actual client, childId is null)
            let selectedClientEmployee = {}
            selectedClientEmployee = clientEmployee.filter(ce => ce.childId === null)[0];
            
            /*
            Initial case
            or
            Selected employee is "Next available" -> Choose best estimation
            */
            if (selectedClientEmployee?.employeeId === 0) {
              clientEstimation = {...estimations[0]};
              setClientEmployee(clientId, null, clientEstimation.employee_id, clientEstimation);
            }
            /*
            Selected employee is explicitly chosen -> Respect preferences
            */
            else {
              estimations.forEach(estimation => {
                if (estimation.employee_id === selectedClientEmployee.employeeId) {
                  clientEstimation = {...estimation};
                  setClientEmployee(clientId, null, clientEstimation.employee_id, clientEstimation);
                  return;
                }
              })
            }
          }
        }
        // If client has children, by default their staff should be "Next available", too
        if (childrenSelectedForServices.length > 0) {
          this.setState({ useNextAvailableStaffFlow: false });
        }
      }

      // Mandatory to pass clientEmployee here
      const estimationsForChildren = this.getUniqueEstimationsForChildren(clientEmployee, clientEstimation);

      this.setEstimationsForChildren(estimationsForChildren);

      // Finally, everything is mounted for sure
      if (!this.state.useNextAvailableStaffFlow) {
        this.setState({ useNextAvailableStaffFlow: true });
      }
    } else {
      this.refreshEstimations(true);
    }    
  }

  componentDidUpdate(prevProps) {
    const { initialized } = this.state;

    if (
      initialized &&
      !_.isEqual(this.props.clientEmployee, prevProps.clientEmployee)
    ) {
      this.refreshEstimations(false);
    }
  }

  async refreshEstimations(clearOldEstimations) {
    const {
      location,
      clientVariations,
      getStaffEstimations,
      clientInformation,
      hasServicesForClient,
      childrenSelectedForServices,
      employees,
      clientEmployee,
    } = this.props;

    const sequences = [];

    const clientId = clientInformation.id;

    if (hasServicesForClient) {
      sequences.push(
        generateSequence(
          clientId,
          null,
          clientVariations,
          clientEmployee,
          employees
        )
      );
    }

    childrenSelectedForServices.forEach(childId => {
      sequences.push(
        generateSequence(
          clientId,
          childId,
          clientVariations,
          clientEmployee,
          employees
        )
      );
    });

    const sequncesForNextAvailableStaff = sequences.reduce((acc, val) => {
      const sequence = {
        ...val,
        for_employee_ids: [],
      }

      acc.push(sequence)
      return acc
    }, [])

    await getStaffEstimations(clearOldEstimations, location, [sequences, sequncesForNextAvailableStaff]);
  }

  getUniqueEstimationsForChildren(clientEmployee, clientEstimation) {
    const {
      childrenSelectedForServices,
    } = this.props;

    const estimationsForChildren = [];
    let estimationsCopy = [...this.props.staffEstimations];
    // Remove client estimation if exists
    estimationsCopy = estimationsCopy.filter(estimationCopy => !_.isEqual(estimationCopy, clientEstimation));
    
    /*
    Get unique & best available estimation for every child
    */
    childrenSelectedForServices.forEach(childId => {
      // Find previous employee
      let selectedClientEmployee = {};
      if (clientEmployee.length > 0) {
        let filteredClientEmployee = clientEmployee.filter(ce => ce.childId === childId)
        selectedClientEmployee = (filteredClientEmployee && filteredClientEmployee[0]) || {};
      }

      for(let i = 0; i < estimationsCopy.length; i++) {
        /*
        Initial case
        or
        Selected employee is "Next available" => Choose best estimation
        */
        if (Object.keys(selectedClientEmployee)?.length === 0 || selectedClientEmployee?.employeeId === 0) {
          // Find estimation
          // It's enough to look after the same child id
          if (estimationsCopy[i].child_id === childId && estimationsCopy[i]?.chosen_time?.begin_at && estimationsCopy[i]?.employee_id) {
            estimationsForChildren.push(estimationsCopy[i]);

            // Filter out other estimations that we DO NOT WANT to use anymore (Duplicates)
            const previouslySelectedEmployeeId = estimationsCopy[i].employee_id;
            const previouslySelectedStartTime = estimationsCopy[i].chosen_time.begin_at;
            estimationsCopy = estimationsCopy.filter(estimation => {
              if (estimation.employee_id === previouslySelectedEmployeeId && estimation.chosen_time.begin_at === previouslySelectedStartTime) {
                return false;
              }
              return true;
            })

            break;
          }
        }
        /*
        Selected employee is explicitly chosen -> Respect client's preferences
        */
        else {
          // Find estimation
          // It's not enough to look after the same child id, employee id must be considered, too
          if (estimationsCopy[i].child_id === childId && estimationsCopy[i]?.employee_id === selectedClientEmployee.employeeId && estimationsCopy[i]?.chosen_time?.begin_at && estimationsCopy[i]?.employee_id) {
            estimationsForChildren.push(estimationsCopy[i]);

            // Filter out other estimations that we DO NOT WANT to use anymore (Duplicates)
            const previouslySelectedEmployeeId = estimationsCopy[i].employee_id;
            const previouslySelectedStartTime = estimationsCopy[i].chosen_time.begin_at;
            estimationsCopy = estimationsCopy.filter(estimation => {
              if (estimation.employee_id === previouslySelectedEmployeeId && estimation.chosen_time.begin_at === previouslySelectedStartTime) {
                return false;
              }
              return true;
            })

            break;
          }
        }
      }
    })

    return estimationsForChildren;
  }

  setEstimationsForChildren(estimationsForChildren) {
    const {
      setClientEmployee,
      childrenSelectedForServices,
      clientInformation
    } = this.props;

    const clientId = clientInformation.id;

    childrenSelectedForServices.forEach(childId => {
      const estimations = this.getClientEstimations(estimationsForChildren, clientId, childId);

      if (estimations.length > 0 && !estimations[0].isUnavailable) {
        if (!this.state.useNextAvailableStaffFlow) {
          // By default, "Next available" option is chosen => employee id must be 0
          setClientEmployee(clientId, childId, 0, estimations[0]);
        }
        else {
          // Find correspondent estimation
          estimationsForChildren.forEach(estimationForChild => {
            if (estimationForChild.child_id === childId) {
              let isUpdated = false;
              estimations.forEach(estimation => {
                if (estimationForChild.chosen_time.begin_at === estimation.begin_at && estimationForChild.employee_id === estimation.real_employee_id && !isUpdated) {
                  setClientEmployee(clientId, childId, estimation.real_employee_id, estimation);
                  isUpdated = true;
                }
              })
            }
          })
        }
      }
    });
  }

  getRemainingText(remaining, beginAt) {
    const { bookRealAppointments, location } = this.props;

    if (bookRealAppointments) {
      return `(${t('staff_selection.next_available')} ${moment(beginAt)
        .tz(location.time_zone)
        .format('h:mm A')})`;
    }

    return (
      '(' +
      t('staff_selection.est_wait') +
      ': ' +
      translateToMins((remaining || 0) * 1000, 'm', 'h') +
      ')'
    );
  }

  getClientEstimations(estimations, clientId, childId) {
    const { employees, bookRealAppointments } = this.props;

    let clientEstimations = estimations.filter(ce =>
      ce.client_id === clientId &&
      ce.child_id === childId
    );

    if (!bookRealAppointments) {
      clientEstimations = estimations.filter(ce => ce.employee_id != null);
    }

    if (clientEstimations && clientEstimations.length > 0) {
      const estimationsHashByEmployeeId = {};

      const estimations = clientEstimations
        .filter(
          ce => ce.chosen_time != null && ce.chosen_time.employee_id != null
        )
        .map(ce => ({
          employee_id: ce.chosen_time.employee_id,
          real_employee_id: ce.chosen_time.employee_id,
          employee_name: getEmployeeName(ce.chosen_time.employee_id, employees),
          remaining: ce.remaining,
          begin_at: ce.chosen_time.begin_at,
          end_at: ce.chosen_time.end_at,
          end_at_without_buffer: ce.chosen_time.end_at_without_buffer,
          over_waitlist_limit: ce.over_waitlist_limit,
          remaining_text: this.getRemainingText(
            ce.remaining,
            ce.chosen_time.begin_at
          ),
        }))
        .filter((ce => {
          if (!estimationsHashByEmployeeId[`${ce.real_employee_id}_${ce.begin_at}`]) {
            estimationsHashByEmployeeId[`${ce.real_employee_id}_${ce.begin_at}`] = true;
            return true;
          }

          return false;
        }));

      // we have to select actual employee to send to api
      if (bookRealAppointments) {
        let minEstimation = {};
        if (estimations.length > 0) {
          minEstimation = estimations[0];

          estimations.forEach(e => {
            if (e.begin_at < minEstimation.begin_at) minEstimation = e;
          });
        }

        estimations.unshift({
          isUnavailable: minEstimation.employee_id == null,
          employee_id: 0,
          real_employee_id: minEstimation.employee_id,
          employee_name:
            minEstimation.employee_id != null
              ? t('staff_selection.next_available')
              : t('staff_selection.none_available'),
          remaining: minEstimation.remaining,
          begin_at: minEstimation.begin_at,
          end_at: minEstimation.end_at,
          remaining_text:
            minEstimation.employee_id != null
              ? this.getRemainingText(
                  minEstimation.remaining,
                  minEstimation.begin_at
                )
              : +t('staff_selection.n_a'),
        });

        return estimations;
      } else {
        let minRemaining = Math.min(...estimations.filter(e => e.remaining !== null).map(e => e.remaining));
        if (minRemaining === Infinity || isNaN(minRemaining))
          minRemaining = null;

        estimations.unshift({
          isUnavailable: estimations.length === 0,
          employee_id: 0,
          employee_name:
            estimations.length > 0
              ? t('staff_selection.next_available')
              : t('staff_selection.none_available'),
          remaining: minRemaining,
          remaining_text: minRemaining
            ? '(' +
              t('staff_selection.est_wait') +
              ': ' +
              translateToMins(minRemaining * 1000, 'm', 'h') +
              ')'
            : +t('staff_selection.n_a'),
        });
      }

      return estimations;
    } else {
      return [];
    }
  }

  renderFamilyMember(clientId, childId) {
    const {
      clientInformation,
      serviceProviderTerm,
      clientEmployee,
      setClientEmployee,
      staffEstimationsLoading,
    } = this.props;

    let firstName = '';

    if (!childId) {
      firstName = clientInformation.first_name;
    } else {
      const child = clientInformation.children.filter(c => c.id === childId)[0];
      if (child) {
        firstName = child.first_name;
      }
    }

    const estimations = this.getClientEstimations(this.props.staffEstimations, clientId, childId);

    const selectedEmployee = clientEmployee.filter(
      ce => ce.clientId === clientId && ce.childId === childId
    )[0];

    const selectedEmployeeId = selectedEmployee
      ? selectedEmployee.employeeId
        ? selectedEmployee.employeeId
        : 0
      : 0;

    return (
      <div
        key={childId}
        style={{
          pointerEvents: staffEstimationsLoading
            ? t('staff_selection.none')
            : null,
        }}
      >
        <div className="staff-selection-person">{`${serviceProviderTerm} ${t('services.for')} ${firstName}`}</div>

        {/* this dropdown works in a very weird way */}
        <Dropdown
          ui={{}}
          list={estimations.map(e => e.employee_id)}
          format={id => {
            const e = estimations.filter(est => est.employee_id === id)[0];

            if (!e) return <i className="fas fa-spinner fa-pulse" />;

            if (e.over_waitlist_limit) {
              return this.renderEmployeeWaitlistFull(
                e.employee_id,
                e.employee_name,
              )
            }

            return this.renderEstimation(
              e.employee_id,
              e.employee_name,
              e.remaining_text
            );
          }}
          onChange={id => {
            const e = estimations.find(est => est.employee_id === id);
            if (!e || e.over_waitlist_limit) {
              return;
            }
            setClientEmployee(
              clientId,
              childId,
              id === 0 ? null : id,
              estimations.filter(e => e.employee_id === id)[0]
            );
          }}
          value={selectedEmployeeId}
          className="staff-selection-dropdown"
        />
      </div>
    );
  }

  async continueClick() {
    this.setState({ submitting: true});
    await this.updateStaffEstimations();
    this.setState({ 
      initialized: false,
      submitting: false,
     }); 
    this.props.history.push('/summary');
  }

  renderEmployeeWaitlistFull(employeeId, name, remainingText) {
    return (
      <div key={employeeId} className="staff-selection-dropdown-item">
        <div className="staff-selection-dropdown-item-left opacity-60">
          {name}
        </div>
        <div className="staff-selection-dropdown-item-right warning-message">
          {t('staff_selection.waitlist_full')}
        </div>
      </div>
    );
  }

  renderEstimation(employeeId, name, remainingText) {
    const { staffEstimationsLoading } = this.props;

    return (
      <div key={employeeId} className="staff-selection-dropdown-item">
        <div className="staff-selection-dropdown-item-left">
          {staffEstimationsLoading && (
            <React.Fragment>
              <i className="fas fa-spinner fa-pulse" />{' '}
            </React.Fragment>
          )}
          {name}
        </div>
        <div className="staff-selection-dropdown-item-right">
          {remainingText}
        </div>
      </div>
    );
  }

  allClientsHaveEstimations() {
    const {
      clientInformation,
      childrenSelectedForServices,
      hasServicesForClient,
    } = this.props;

    let hasEstimations = true;

    if (hasServicesForClient) {
      const clientEstimations = this.getClientEstimations(
        this.props.staffEstimations,
        clientInformation.id,
        null
      );
      if (clientEstimations.length === 0 || clientEstimations[0].isUnavailable)
        hasEstimations = false;
    }

    childrenSelectedForServices.forEach(childId => {
      const childEstimations = this.getClientEstimations(
        this.props.staffEstimations,
        clientInformation.id,
        childId
      );

      if (childEstimations.length === 0 || childEstimations[0].isUnavailable)
        hasEstimations = false;
    });

    return hasEstimations;
  }

  render() {
    const {
      clientInformation,
      hasServicesForClient,
      childrenSelectedForServices,
      serviceProviderTerm,
    } = this.props;

    const clientId = clientInformation.id;

    const isAvailable = this.allClientsHaveEstimations();

    return (
      <React.Fragment>
        <Backdrop id="backdrop" />
        <div className="scroll-container content-container">
          <div className="page-container">
            <div className="staff-selection-title">
              {t('staff_selection.please_select')} {serviceProviderTerm}
            </div>
            <div className="content">
              <div className="staff-selection-container">
                {hasServicesForClient &&
                  this.renderFamilyMember(clientId, null)}
                {childrenSelectedForServices.map(childId =>
                  this.renderFamilyMember(clientId, childId)
                )}
              </div>
              <ActionButton 
                className="staff-selection-continue-button"
                onClick={this.continueClick}
                disabled={this.state.submitting || !isAvailable}
              >
                { t('services.continue') }
                {this.state.submitting && (
                  <React.Fragment>
                    {' '}
                    <i className="fas fa-spinner fa-pulse" />
                  </React.Fragment>
                )}
              </ActionButton>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

function mapStateToProps(state, ownProps) {
  return {
    location: state.app.location,
    variations: getBookableVariations(state.service.variations),
    clientVariations: state.client.clientVariations,
    clientInformation: state.client.clientInformation,
    staffEstimations: state.appointment.staffEstimations,
    staffEstimationsLoading: state.appointment.staffEstimationsLoading,
    hasServicesForClient: state.client.hasServicesForClient,
    childrenSelectedForServices: state.client.childrenSelectedForServices,
    serviceProviderTerm: state.app.serviceProviderTerm,
    employees: state.employee.employees,
    clientEmployee: state.client.clientEmployee,
    bookRealAppointments:
      state.app.businessInfo.company.real_appointments_on_kiosk,
  };
}

export default withRouter(
  connect(
    mapStateToProps,
    {
      getVariations,
      createAppointment,
      getStaffEstimations,
      setClientEmployee,
      clearClientEmployee,
    }
  )(StaffSelection)
);
